import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";

const VERSION_URL = "/version.json";
const POLL_INTERVAL_MS = 2 * 60 * 1000;
const SNACKBAR_KEY = "rc-update-available";

async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const { buildId } = (await res.json()) as { buildId?: string };
    return buildId ?? null;
  } catch {
    return null;
  }
}

/**
 * Polls version.json and shows a persistent "Reload" toast when a newer build
 * has been deployed than the one the running app was built from.
 *
 * No-op outside production builds (version.json is only emitted by `vite build`).
 * Polls on an interval and whenever the tab regains visibility — the latter is
 * the primary trigger on mobile, where tabs sleep rather than poll.
 */
export function useVersionCheck(): void {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const notified = useRef(false);

  useEffect(() => {
    const currentBuildId = import.meta.env.VITE_BUILD_ID as string | undefined;
    if (!import.meta.env.PROD || !currentBuildId) return;

    const check = async () => {
      if (notified.current) return;
      const deployed = await fetchDeployedBuildId();
      if (!deployed || deployed === currentBuildId) return;

      notified.current = true;
      enqueueSnackbar(t("update.available"), {
        key: SNACKBAR_KEY,
        variant: "info",
        persist: true,
        preventDuplicate: true,
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        action: () => (
          <Button color="inherit" size="small" onClick={() => window.location.reload()}>
            {t("update.reload")}
          </Button>
        ),
      });
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };

    void check();
    const interval = window.setInterval(() => void check(), POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enqueueSnackbar, t]);
}
